import { criarClienteApi, ErroApiNyroh, type ClienteApi } from '@nyroh/contracts';
import { createAuthClient } from 'better-auth/react';

import { clienteMigracao } from './adaptador-migracao';
import { exigirEnv } from './env';

/**
 * ============================================================================
 * A CAMADA DE DADOS DA CAPTAÇÃO
 * ============================================================================
 *
 * Substitui `supabase-service.ts`, e com ele três coisas de uma vez:
 *
 * 1. O `createClient(url, anonKey)` no navegador. A chave anônima ia no bundle,
 *    e com as policies em `USING (true)` do outro lado ela equivalia a escrita e
 *    exclusão total na base de leads.
 *
 * 2. O array `configs` com um item só. Ele existia "para o dia em que houver mais
 *    de um destino de escrita" — um multi-banco que nunca veio, mas que deixou
 *    `syncToAll` espalhado por 20 pontos de chamada. Um lead tem um lugar.
 *
 * 3. `uploadLeadPhoto`, que convertia base64 e mandava para o bucket
 *    `lead-photos`. As fotos continuam saindo do navegador para o object
 *    storage — mas por URL pré-assinada e com escrita autenticada, e o que fica
 *    gravado na linha é a CHAVE, nunca o base64.
 *
 * As funções abaixo têm nome de operação, não de tabela. Isso é deliberado: um
 * `client.from('x').select()` genérico convida a escrever consulta nova a partir
 * da tela, que é o hábito que este trabalho está removendo.
 */

const API_URL = exigirEnv(
  'VITE_API_URL',
  import.meta.env.VITE_API_URL,
  'URL da API Nyroh (ex.: http://localhost:3333 no ambiente local)',
);

export const CDN_ASSETS = exigirEnv(
  'VITE_CDN_BASE_ASSETS',
  import.meta.env.VITE_CDN_BASE_ASSETS,
  'base pública do bucket de imagens (fotos de lead)',
);

export const auth = createAuthClient({
  baseURL: `${API_URL}/api/auth`,
  fetchOptions: { credentials: 'include' },
});

export const api: ClienteApi = criarClienteApi({
  baseUrl: API_URL,
  comCredenciais: true,
  aoErro: (erro) => {
    console.error(`[api] ${erro.rota ?? '?'} -> ${erro.codigo}: ${erro.message}`);
  },
});

export { ErroApiNyroh };

/**
 * Compatibilidade com os pontos que ainda chamam `syncToAll`.
 *
 * Antes ele disparava a mesma operação contra N clientes Supabase e devolvia
 * `Promise.allSettled` — o que significa que uma falha de escrita ficava dentro
 * de um resultado `rejected` que ninguém inspecionava. Agora há um destino só, e
 * o erro SOBE: se a gravação falhar, quem chamou fica sabendo.
 */
export async function syncToAll<T>(
  operacao: (client: typeof clienteMigracao) => Promise<T>,
): Promise<T> {
  return operacao(clienteMigracao);
}

// ---------------------------------------------------------------- campanhas --

export interface PassoLocal {
  id?: string;
  targetId: string;
  targetName: string;
  regionId: string;
  regionName: string;
  cityName: string;
  status: string;
  leadsFound: number;
  lat?: number | null;
  lng?: number | null;
  completedAt?: number | null;
  erro?: string | null;
}

export interface CampanhaLocal {
  id: string;
  name: string;
  status: string;
  currentStepIndex: number;
  totalLeads: number;
  autoEnrich?: boolean;
  createdAt: number;
  updatedAt: number;
  steps: PassoLocal[];
}

const STATUS_CAMPANHA: Record<string, 'rascunho' | 'rodando' | 'pausada' | 'concluida' | 'falhou'> =
  {
    draft: 'rascunho',
    running: 'rodando',
    active: 'rodando',
    paused: 'pausada',
    completed: 'concluida',
    finished: 'concluida',
    failed: 'falhou',
    error: 'falhou',
  };

const STATUS_PASSO: Record<string, 'pendente' | 'rodando' | 'concluido' | 'falhou' | 'pulado'> = {
  pending: 'pendente',
  running: 'rodando',
  completed: 'concluido',
  done: 'concluido',
  failed: 'falhou',
  error: 'falhou',
  skipped: 'pulado',
};

function paraStatusCampanha(s: string) {
  return STATUS_CAMPANHA[s] ?? 'rascunho';
}
function paraStatusPasso(s: string) {
  return STATUS_PASSO[s] ?? 'pendente';
}

/** Salva campanha e passos numa transação do servidor. */
export async function salvarCampanha(
  c: CampanhaLocal,
  substituirPassos = false,
): Promise<void> {
  await api.chamar('campanhas.salvar', {
    params: { id: c.id },
    corpo: {
      id: c.id,
      nome: c.name,
      status: paraStatusCampanha(c.status),
      indicePassoAtual: c.currentStepIndex ?? 0,
      totalLeads: c.totalLeads ?? 0,
      enriquecerAuto: c.autoEnrich ?? false,
      substituirPassos,
      passos: (c.steps ?? []).map((s) => ({
        alvoId: s.targetId,
        alvoNome: s.targetName,
        regiaoId: s.regionId,
        regiaoNome: s.regionName,
        cidadeNome: s.cityName,
        status: paraStatusPasso(s.status),
        leadsEncontrados: s.leadsFound ?? 0,
        lat: s.lat ?? null,
        lng: s.lng ?? null,
        concluidoEm: s.completedAt ? new Date(s.completedAt).toISOString() : null,
        erro: s.erro ?? null,
      })),
    },
  });
}

/** Lista campanhas com os passos, na forma que as telas já usam. */
export async function listarCampanhas(): Promise<CampanhaLocal[]> {
  const remotas = await api.chamar('campanhas.listar', {});

  const inverso = (mapa: Record<string, string>, valor: string) =>
    Object.entries(mapa).find(([, v]) => v === valor)?.[0] ?? valor;

  return remotas.map((c) => ({
    id: c.id,
    name: c.nome,
    status: inverso(STATUS_CAMPANHA, c.status),
    currentStepIndex: c.indicePassoAtual,
    totalLeads: c.totalLeads,
    autoEnrich: c.enriquecerAuto,
    createdAt: new Date(c.criadoEm).getTime(),
    updatedAt: new Date(c.atualizadoEm).getTime(),
    steps: c.passos.map((p) => ({
      id: p.id,
      targetId: p.alvoId,
      targetName: p.alvoNome,
      regionId: p.regiaoId,
      regionName: p.regiaoNome,
      cityName: p.cidadeNome,
      status: inverso(STATUS_PASSO, p.status),
      leadsFound: p.leadsEncontrados,
      lat: p.lat,
      lng: p.lng,
      completedAt: p.concluidoEm ? new Date(p.concluidoEm).getTime() : null,
      erro: p.erro,
    })),
  }));
}

export async function excluirCampanha(id: string): Promise<void> {
  await api.chamar('campanhas.excluir', { params: { id } });
}

/**
 * Atualiza o progresso de um passo.
 *
 * `erro` existe aqui por causa do BUG-6: `maps-service.ts` resolvia qualquer
 * status não-OK do Places como lista vazia, então `REQUEST_DENIED` e "não achei
 * nada" eram indistinguíveis — e a campanha consumia etapas sem capturar nada.
 * Agora a causa fica gravada no passo.
 */
export async function atualizarPasso(
  campanhaId: string,
  passoId: string,
  dados: { status?: string; leadsFound?: number; erro?: string | null; completedAt?: number | null },
): Promise<void> {
  await api.chamar('campanhas.atualizarPasso', {
    params: { id: campanhaId, passoId },
    corpo: {
      status: dados.status ? paraStatusPasso(dados.status) : undefined,
      leadsEncontrados: dados.leadsFound,
      erro: dados.erro,
      concluidoEm:
        dados.completedAt === undefined
          ? undefined
          : dados.completedAt
            ? new Date(dados.completedAt).toISOString()
            : null,
    },
  });
}

// -------------------------------------------------------------------- leads --

export interface LeadLocal {
  place_id: string;
  name: string;
  formatted_address?: string | null;
  formatted_phone_number?: string | null;
  website?: string | null;
  rating?: number | null;
  user_ratings_total?: number | null;
  types?: string[];
  campaignId?: string | null;
  geometry?: unknown;
  photos?: string[];
  whatsappData?: unknown;
  scannedAt?: number;
}

export async function listarLeads(limite = 500, campanhaId?: string): Promise<LeadLocal[]> {
  const acumulado: LeadLocal[] = [];
  let cursor: string | undefined;

  // Pagina por cursor até o limite pedido. O `.limit(500)` de antes era o teto do
  // cliente; agora o teto é do servidor e a paginação é explícita.
  while (acumulado.length < limite) {
    const pagina = await api.chamar('leads.listar', {
      query: {
        limite: Math.min(200, limite - acumulado.length),
        cursor,
        campanhaId,
      },
    });
    for (const l of pagina.itens) {
      acumulado.push({
        place_id: l.placeId,
        name: l.nome,
        formatted_address: l.endereco,
        formatted_phone_number: l.telefone,
        website: l.website,
        rating: l.nota,
        user_ratings_total: l.totalAvaliacoes,
        types: l.tipos,
        campaignId: l.campanhaId,
        geometry: l.geometria,
        photos: l.fotos.map(urlDaFoto),
        whatsappData: l.dadosWhatsapp,
        scannedAt: new Date(l.varridoEm).getTime(),
      });
    }
    if (!pagina.proximoCursor) break;
    cursor = pagina.proximoCursor;
  }

  return acumulado;
}

/**
 * Grava leads em lote.
 *
 * `place_id` é a chave primária, então isto é upsert: reprocessar a mesma região
 * ATUALIZA em vez de duplicar. É o que torna a varredura retentável — e é
 * pré-requisito para ela sair do navegador e virar job da fila.
 */
export async function salvarLeads(leads: LeadLocal[]): Promise<{ salvos: number; atualizados: number }> {
  let salvos = 0;
  let atualizados = 0;

  for (let i = 0; i < leads.length; i += 200) {
    const lote = leads.slice(i, i + 200);
    const r = await api.chamar('leads.salvar', {
      corpo: {
        leads: lote.map((l) => ({
          placeId: l.place_id,
          nome: l.name,
          endereco: l.formatted_address ?? null,
          telefone: l.formatted_phone_number ?? null,
          website: l.website ?? null,
          nota: l.rating ?? null,
          totalAvaliacoes: l.user_ratings_total ?? null,
          tipos: l.types ?? [],
          campanhaId: l.campaignId ?? null,
          geometria: l.geometry ?? null,
          // Guarda a CHAVE do objeto, não a URL: trocar o domínio do CDN não
          // pode exigir reescrever a base de leads.
          fotos: (l.photos ?? []).map(chaveDaFoto),
          dadosWhatsapp: l.whatsappData ?? null,
          varridoEm: l.scannedAt ? new Date(l.scannedAt).toISOString() : undefined,
        })),
      },
    });
    salvos += r.salvos;
    atualizados += r.atualizados;
  }

  return { salvos, atualizados };
}

export async function excluirLeads(placeIds: string[]): Promise<number> {
  if (placeIds.length === 0) return 0;
  let total = 0;
  for (let i = 0; i < placeIds.length; i += 500) {
    const r = await api.chamar('leads.excluir', {
      corpo: { placeIds: placeIds.slice(i, i + 500) },
    });
    total += r.excluidos;
  }
  return total;
}

/**
 * Sobe a foto de um lead para o object storage.
 *
 * Substitui `uploadLeadPhoto`, que gravava no bucket `lead-photos`. A diferença
 * que importa: aquele bucket foi criado com política de escrita anônima — o SQL
 * de instrução no próprio código mandava criar `"Insert for anyone"` e
 * `"Delete for anyone"` em `storage.objects`. Qualquer visitante podia apagar as
 * fotos de todos os leads.
 */
export async function enviarFotoDeLead(
  placeId: string,
  base64: string,
  sufixo = '',
): Promise<string | null> {
  if (!base64 || !base64.includes('base64')) return null;
  try {
    const r = await api.chamar('assets.enviarBase64', {
      corpo: { placeId, conteudoBase64: base64, sufixo, mime: 'image/jpeg' },
    });
    return r.url;
  } catch (erro) {
    console.error('Falha ao enviar foto do lead:', erro);
    return null;
  }
}

/** URL pública a partir da chave guardada. */
export function urlDaFoto(chave: string): string {
  if (chave.startsWith('http')) return chave;
  return `${CDN_ASSETS.replace(/\/+$/, '')}/${chave.replace(/^\/+/, '')}`;
}

/** Caminho inverso: extrai a chave de uma URL de CDN. */
export function chaveDaFoto(url: string): string {
  if (!url.startsWith('http')) return url;
  const base = CDN_ASSETS.replace(/\/+$/, '');
  return url.startsWith(base) ? url.slice(base.length + 1) : url;
}

// ------------------------------------------------- clientes já cadastrados --

/**
 * Clientes já cadastrados na plataforma, para a higienização de campanha.
 *
 * Substitui um `fetch` para `https://www.nyroh.com.br/api/clients-directory`
 * com uma chave `nyroh_live_…` FIXA no código — que ia no bundle e só podia ser
 * revogada com deploy. Agora usa a sessão do operador, como todo o resto.
 */
export async function listarClientesCadastrados(limite = 2000): Promise<
  Array<{ id: string; nome: string; telefone: string | null; categoria: string | null; status: string }>
> {
  const acumulado: Array<{
    id: string;
    nome: string;
    telefone: string | null;
    categoria: string | null;
    status: string;
  }> = [];
  let cursor: string | undefined;

  while (acumulado.length < limite) {
    const pagina = await api.chamar('clientes.listar', {
      query: { limite: Math.min(200, limite - acumulado.length), cursor, periodo: 'todos' },
    });
    for (const c of pagina.itens) {
      acumulado.push({
        id: c.id,
        nome: c.nome,
        telefone: c.telefone,
        categoria: c.categoria,
        status: c.status,
      });
    }
    if (!pagina.proximoCursor) break;
    cursor = pagina.proximoCursor;
  }

  return acumulado;
}

// -------------------------------------------------------------- json configs --

export interface ConfigJsonLocal {
  id: string;
  name: string;
  type: string;
  description?: string;
  content: unknown;
  item_count?: number;
  created_at?: string;
  updated_at?: string;
}

export async function listarConfigs(): Promise<ConfigJsonLocal[]> {
  const remotas = await api.chamar('configsJson.listar', {});
  return remotas.map((c) => {
    const conteudo = c.conteudo as Record<string, unknown> | null;
    return {
      id: c.id,
      name: c.nome,
      type: c.tipo,
      description: (conteudo?.description as string) ?? '',
      content: conteudo?.content ?? conteudo,
      item_count: (conteudo?.item_count as number) ?? 0,
      created_at: c.criadoEm,
      updated_at: c.criadoEm,
    };
  });
}

export async function salvarConfig(item: ConfigJsonLocal): Promise<void> {
  await api.chamar('configsJson.salvar', {
    corpo: {
      id: item.id,
      nome: item.name,
      tipo: item.type,
      conteudo: {
        description: item.description,
        content: item.content,
        item_count: item.item_count,
        updated_at: new Date().toISOString(),
      },
    },
  });
}

export async function excluirConfig(id: string): Promise<void> {
  await api.chamar('configsJson.excluir', { params: { id } });
}
